/**
 * The charts an article can carry, placed in its MDX as `<Figure id="..." />`.
 * Every number in one comes from the source named under the chart. A chart
 * whose source line links that source may be the only place a page shows its
 * numbers; any other chart only draws numbers the page states in its text or
 * tables. `article.test.ts` checks this.
 */

export type FigureRow = {
  label: string;
  /** A second line under the label. */
  detail?: string;
  value: number;
  /** The value in the page's own words, set at the end of the bar. */
  display: string;
};

type FigureBase = {
  title: string;
  caption?: string;
  /** The column headings of the table that stands in for the chart in Markdown. */
  columns: [string, string];
  rows: FigureRow[];
  source: { text: string; href?: string };
};

/** Horizontal bars from zero. */
export type BarFigure = FigureBase & {
  kind: "bars";
  max: number;
  ticks: number[];
  /** Vertical lines across the bars, such as a cap that applies to every row. */
  refs?: { value: number; label: string; tone: "accent" | "muted" }[];
};

/** A dot per row on a log axis, for values that span several powers of ten. */
export type LogDotFigure = FigureBase & {
  kind: "log-dots";
  /** Powers of ten from the first gridline to the last. */
  ticks: number[];
};

export type ArticleFigure = BarFigure | LogDotFigure;

const CHECKED = "checked October 15, 2026";
const PRICES_CHECKED = "checked October 15, 2026";

export const FIGURES = {
  "linkedin-connection-caps": {
    kind: "bars",
    title: "Connection requests a day, by sending schedule",
    caption:
      "The default weekly cap of 100 spread over the days an account sends. The line is the default daily limit; the engine sends the lower of the two.",
    columns: ["Sending schedule", "Most connection requests a day"],
    rows: [
      { label: "Monday to Friday", value: 20, display: "20" },
      { label: "Every day", value: 14, display: "14" },
    ],
    max: 25,
    ticks: [0, 5, 10, 15, 20, 25],
    refs: [{ value: 20, label: "Daily limit: 20", tone: "accent" }],
    source: { text: `Caps enforced by Bavlio's outreach engine in production, ${CHECKED}.` },
  },
  "linkedin-daily-limits": {
    kind: "bars",
    title: "Most actions a day, per LinkedIn account",
    caption: "Connection requests also stop at 100 per rolling 7 days by default.",
    columns: ["Action", "Most a day"],
    rows: [
      { label: "Connection requests", detail: "Monday to Friday", value: 20, display: "20" },
      { label: "Connection requests", detail: "Every day", value: 14, display: "14" },
      { label: "Messages", detail: "By default", value: 100, display: "100" },
      { label: "LinkedIn searches", detail: "10 an hour", value: 50, display: "50" },
      { label: "Bulk profile lookups", detail: "One at a time", value: 20, display: "20" },
    ],
    max: 100,
    ticks: [0, 25, 50, 75, 100],
    source: {
      text: `Limits enforced by Bavlio's outreach engine in production, ${CHECKED}.`,
      href: "/blog/linkedin-automation-limits",
    },
  },
  "mcp-tools-by-group": {
    kind: "bars",
    title: "The new server's 41 tools, by group",
    columns: ["Group", "Tools"],
    rows: [
      { label: "Campaigns and leads", value: 10, display: "10" },
      { label: "Account and API keys", value: 7, display: "7" },
      { label: "Meetings and calendar", value: 6, display: "6" },
      { label: "Workspace", value: 4, display: "4" },
      { label: "LinkedIn account", value: 4, display: "4" },
      { label: "Inbox", value: 4, display: "4" },
      { label: "Billing", value: 4, display: "4" },
      { label: "Sequence", value: 2, display: "2" },
    ],
    max: 10,
    ticks: [0, 2, 4, 6, 8, 10],
    source: { text: `The tool list of the MCP server in Bavlio's new version, ${CHECKED}.` },
  },
  "linkedin-warm-up-new-version": {
    kind: "bars",
    title: "Most connection requests a day, as a new account warms up",
    caption: "Connection requests also stop at 100 a week once the warm-up ends.",
    columns: ["Account age", "Most connection requests a day"],
    rows: [
      { label: "Days 1 to 7", value: 6, display: "6" },
      { label: "Days 8 to 14", value: 12, display: "12" },
      { label: "Days 15 to 21", value: 18, display: "18" },
      { label: "From day 22", value: 25, display: "25" },
    ],
    max: 25,
    ticks: [0, 5, 10, 15, 20, 25],
    source: { text: `Limits in the outreach engine of Bavlio's new version, ${CHECKED}.` },
  },
  "inboxes-per-plan": {
    kind: "log-dots",
    title: "Inboxes per domain on each Bavimail plan",
    caption: "Each gridline is 10 times the one before.",
    columns: ["Plan", "Inboxes per domain"],
    rows: [
      { label: "Free", detail: "6,000 emails a month", value: 3, display: "3" },
      { label: "Developer, $5 a month", detail: "50,000 emails a month", value: 10, display: "10" },
      {
        label: "Startup, $50 a month",
        detail: "300,000 emails a month",
        value: 300,
        display: "300",
      },
      {
        label: "Scale, $500 a month",
        detail: "2,500,000 emails a month",
        value: 1000,
        display: "1,000",
      },
    ],
    ticks: [1, 10, 100, 1000],
    source: { text: `Bavimail pricing, ${CHECKED}.`, href: "/pricing" },
  },
  "cost-of-50k-emails": {
    kind: "bars",
    title: "What 50,000 emails a month costs",
    caption:
      "AgentMail on Developer plus 40 add-ons of 1,000 emails; Amazon SES at its à la carte rate.",
    columns: ["Provider", "Per month"],
    rows: [
      { label: "AgentMail", detail: "Developer plus add-ons", value: 100, display: "$100" },
      { label: "Amazon SES", detail: "À la carte", value: 5, display: "$5" },
      { label: "Bavimail", detail: "Developer", value: 5, display: "$5" },
      { label: "Resend", detail: "Pro", value: 20, display: "$20" },
    ],
    max: 100,
    ticks: [0, 25, 50, 75, 100],
    source: { text: `Each provider's pricing page, ${PRICES_CHECKED}.` },
  },
  "emails-for-20-dollars": {
    kind: "bars",
    title: "Emails a month included for $20",
    columns: ["Plan", "Emails a month"],
    rows: [
      { label: "AgentMail", detail: "Developer, 10 inboxes", value: 10000, display: "10,000" },
      { label: "Resend", detail: "Pro, 3 inboxes", value: 50000, display: "50,000" },
    ],
    max: 50000,
    ticks: [0, 10000, 20000, 30000, 40000, 50000],
    source: { text: `AgentMail and Resend pricing pages, ${PRICES_CHECKED}.` },
  },
  "entry-plan-prices": {
    kind: "bars",
    title: "The cheapest paid plan, per month",
    caption:
      "Amazon SES has no monthly fee and charges $0.10 per 1,000 emails, so it isn't plotted.",
    columns: ["Provider", "Entry paid plan"],
    rows: [
      { label: "AgentMail", detail: "10,000 emails", value: 20, display: "$20" },
      { label: "Bavimail", detail: "50,000 emails", value: 5, display: "$5" },
      { label: "Mailgun", detail: "10,000 emails", value: 15, display: "$15" },
      { label: "Postmark", detail: "10,000 emails", value: 15, display: "$15" },
      { label: "Resend", detail: "50,000 emails", value: 20, display: "$20" },
    ],
    max: 20,
    ticks: [0, 5, 10, 15, 20],
    source: { text: `Each provider's pricing page, ${PRICES_CHECKED}.` },
  },
  "emails-per-bavimail-plan": {
    kind: "log-dots",
    title: "Emails a month on each Bavimail plan",
    caption: "Each gridline is 10 times the one before.",
    columns: ["Plan", "Emails a month"],
    rows: [
      { label: "Free", value: 6000, display: "6,000" },
      { label: "Developer", detail: "$5 a month", value: 50000, display: "50,000" },
      { label: "Startup", detail: "$50 a month", value: 300000, display: "300,000" },
      { label: "Scale", detail: "$500 a month", value: 2500000, display: "2,500,000" },
    ],
    ticks: [1000, 10000, 100000, 1000000, 10000000],
    source: { text: `Bavimail pricing, ${PRICES_CHECKED}.`, href: "/pricing" },
  },
} satisfies Record<string, ArticleFigure>;

export type FigureId = keyof typeof FIGURES;

export function isFigureId(id: string): id is FigureId {
  return Object.hasOwn(FIGURES, id);
}

/** The chart as a Markdown table, for the page's Markdown and the LLM exports. */
export function figureMarkdown(figure: ArticleFigure): string {
  const rows = figure.rows.map(
    (row) => `| ${row.label}${row.detail ? ` (${row.detail})` : ""} | ${row.display} |`,
  );
  const source = figure.source.href
    ? `Source: [${figure.source.text}](${figure.source.href})`
    : `Source: ${figure.source.text}`;
  return [
    `**${figure.title}**`,
    "",
    `| ${figure.columns[0]} | ${figure.columns[1]} |`,
    "| --- | --- |",
    ...rows,
    "",
    ...(figure.caption ? [figure.caption, ""] : []),
    source,
  ].join("\n");
}
